import React, { useEffect, useRef, useState } from "react";
import { Button } from "../components/Button";
import { Input, Form } from "../components/Form";
import { Magic } from "magic-sdk";
import { DialogTakeover } from "../components/Dialog";
import { useToasts } from "react-toast-notifications";

export default function Login({ checkAuth }: { checkAuth: () => void }) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sentEmail, setSentEmail] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [email, setEmail] = useState("");
  const userInput = useRef<HTMLInputElement>(null);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setEmail(e.target.value);
  }

  function resetForm() {
    setSentEmail(false);
    setIsSubmitting(false);
    setErrorMsg("");
    setSuccess(false);
    setEmail("");
    userInput.current?.focus();
  }

  // Adapted from https://github.com/vercel/next.js/blob/canary/examples/with-magic/pages/login.js
  async function submitLogin(e: React.FormEvent) {
    e.preventDefault();
    setIsSubmitting(true);
    setSentEmail(false);
    if (errorMsg) setErrorMsg(""); // Reset

    if (email === "") return;
    const body = {
      email,
    };

    try {
      const key: string =
        process.env.NODE_ENV === "development"
          ? process.env.REACT_APP_MAGIC_PUBLISHABLE_KEY_DEV!
          : process.env.REACT_APP_MAGIC_PUBLISHABLE_KEY_PROD!;

      const magic = new Magic(key);
      setSentEmail(true);
      const didToken = await magic.auth.loginWithMagicLink({
        ...body,
        showUI: false,
      });
      if (didToken) setSuccess(true);
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + didToken,
        },
        body: JSON.stringify(body),
      });

      if (res.status === 200) {
        console.log("Logged in successfully");
        const redirect = new URLSearchParams(window.location.search).get(
          "redirect"
        );
        if (redirect) {
          window.location.replace(redirect);
        } else {
          checkAuth();
        }
      } else {
        throw new Error(await res.text());
      }
    } catch (e) {
      console.error("Magic login error", e);
      setErrorMsg(e.toString());
      setIsSubmitting(false);
    }
  }

  const { addToast } = useToasts();

  useEffect(() => {
    if (!errorMsg) return;
    addToast(errorMsg, { appearance: "error" });
  }, [addToast, errorMsg]);

  if (success)
    return (
      <DialogTakeover
        title="Email confirmed"
        message="Awesome! Taking you to your spaces..."
        role="alert"
      ></DialogTakeover>
    );

  if (sentEmail)
    return (
      <DialogTakeover
        title="Check your email"
        message="We’ve sent you a magic link to your email. Click on the link to continue. Keep this tab open!"
        role="alert"
      >
        <Button size="large" color="secondary" onClick={resetForm}>
          Use a different email
        </Button>
      </DialogTakeover>
    );

  return (
    <DialogTakeover
      title="Welcome to MakeSpace"
      message={`Log in with your email to create new spaces.\n(If someone shared a space with you, just follow their link directly!)
      
If you have questions, reach out to Max or Weiwei :)`}
    >
      <Form onSubmit={submitLogin}>
        <Input
          value={email}
          aria-label="Email"
          type="email"
          name="email"
          placeholder="name@email.com"
          ref={userInput}
          required
          autoFocus
          onChange={onChange}
          disabled={isSubmitting}
        />
        <Button size="large" color="primary" disabled={isSubmitting}>
          {isSubmitting ? "Sending magic link..." : "Send email"}
        </Button>
      </Form>
    </DialogTakeover>
  );
}
